import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Api } from '../../../../../services/interceptor';
import { QUERY_KEY } from '../../../../../services/queryEndPoint';
import {
  TICKER_INTERVAL_OPTIONS,
  type ITickerConfigType,
  type IMatchTickerConfig,
  type TickerIntervalType,
  type TickerRowState,
} from './tickerAutomationConstants';
import './TickerAutomationModal.scss';

/** Event-driven graphic types — no interval needed, fire on the event itself */
const EVENT_DRIVEN_TYPES = new Set([
  'Wicket', 'Four', 'Six', 'PlayerOut', 'Hatrick',
  'TossResult', 'BatsmanMultipleOfFifty', 'TeaBreak',
  'LunchBreak', 'BadLights', 'NewBall', 'InningEndSummary',
]);

interface TickerAutomationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matchId: string | null;
  /** Called on Save with the configured rows — ready to POST when endpoint is available */
  onSave?: (config: TickerRowState[]) => void;
}

export default function TickerAutomationModal({
  open,
  onOpenChange,
  matchId,
  onSave,
}: TickerAutomationModalProps) {
  const [rows, setRows] = useState<TickerRowState[]>([]);

  // Fetch master list of ticker config types
  const { data: configTypes, isLoading: loadingTypes } = useQuery<ITickerConfigType[]>({
    queryKey: [QUERY_KEY.getTickerConfigTypes],
    queryFn: async () => (await Api.get<ITickerConfigType[]>(QUERY_KEY.getTickerConfigTypes)).data,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch existing ticker config for this match (if already scheduled)
  const { data: existingConfig } = useQuery<IMatchTickerConfig[]>({
    queryKey: [QUERY_KEY.getMatchTickerConfig, matchId],
    queryFn: async () =>
      (await Api.get<IMatchTickerConfig[]>(`match/${matchId}/ticker-config`)).data,
    enabled: open && !!matchId,
  });

  // Build rows whenever master list or existing config loads
  const initialRows = useMemo<TickerRowState[]>(() => {
    if (!configTypes?.length) return [];
    const existingMap = new Map(existingConfig?.map((c) => [c.graphicType, c]) ?? []);
    return configTypes.map((ct) => {
      const existing = existingMap.get(ct.graphicType);
      const hasInterval = !EVENT_DRIVEN_TYPES.has(ct.graphicType);
      return {
        graphicType: ct.graphicType,
        graphicTitle: ct.graphicTitle,
        automated: existing?.automated ?? false,
        intervalType: existing?.intervalType ?? ct.intervalType,
        intervalValue: existing?.intervalValue ?? ct.intervalValue,
        hasInterval,
      };
    });
  }, [configTypes, existingConfig]);

  // Sync rows when modal opens or data changes
  useEffect(() => {
    if (open && initialRows.length > 0) {
      setRows(initialRows);
    }
  }, [open, initialRows]);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    if (open) document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [open, onOpenChange]);

  const handleToggle = (graphicType: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.graphicType === graphicType ? { ...r, automated: !r.automated } : r,
      ),
    );
  };

  const handleIntervalType = (graphicType: string, value: TickerIntervalType) => {
    setRows((prev) =>
      prev.map((r) =>
        r.graphicType === graphicType ? { ...r, intervalType: value } : r,
      ),
    );
  };

  const handleIntervalValue = (graphicType: string, value: string) => {
    const n = parseInt(value, 10);
    if (!Number.isFinite(n) || n < 1) return;
    setRows((prev) =>
      prev.map((r) =>
        r.graphicType === graphicType ? { ...r, intervalValue: n } : r,
      ),
    );
  };

  const handleSave = () => {
    onSave?.(rows);
    onOpenChange(false);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="ticker-automation-modal-overlay"
      onClick={() => onOpenChange(false)}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="ticker-automation-modal"
        onClick={(e) => e.stopPropagation()}
        aria-labelledby="ticker-automation-title"
      >
        <div className="ticker-automation-modal-header">
          <h2 id="ticker-automation-title" className="ticker-automation-title">
            Ticker Automation
          </h2>
          <button
            type="button"
            className="ticker-automation-close"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
          >
            &#10005;
          </button>
        </div>

        <div className="ticker-automation-modal-body">
          {loadingTypes ? (
            <p className="ticker-loading">Loading graphics…</p>
          ) : rows.length === 0 ? (
            <p className="ticker-loading">No ticker types available.</p>
          ) : (
            <>
              <div className="ticker-automation-list-header">
                <span>Graphic</span>
                <span>Interval</span>
                <span>Automate</span>
              </div>
              <div className="ticker-automation-list">
                {rows.map((row) => (
                  <TickerRow
                    key={row.graphicType}
                    row={row}
                    onToggle={() => handleToggle(row.graphicType)}
                    onIntervalTypeChange={(v) => handleIntervalType(row.graphicType, v)}
                    onIntervalValueChange={(v) => handleIntervalValue(row.graphicType, v)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="ticker-automation-modal-footer">
          <button
            type="button"
            className="ticker-automation-save"
            onClick={handleSave}
            disabled={loadingTypes}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface TickerRowProps {
  row: TickerRowState;
  onToggle: () => void;
  onIntervalTypeChange: (v: TickerIntervalType) => void;
  onIntervalValueChange: (v: string) => void;
}

function TickerRow({ row, onToggle, onIntervalTypeChange, onIntervalValueChange }: TickerRowProps) {
  return (
    <div className="ticker-row">
      <span className="ticker-row-label">{row.graphicTitle}</span>

      <div className="ticker-interval-controls">
        {row.hasInterval ? (
          <>
            <input
              type="number"
              min={1}
              className="ticker-interval-input"
              value={row.intervalValue}
              onChange={(e) => onIntervalValueChange(e.target.value)}
              aria-label="Interval value"
            />
            <select
              className="ticker-interval-select"
              value={row.intervalType}
              onChange={(e) => onIntervalTypeChange(e.target.value as TickerIntervalType)}
              aria-label="Interval type"
            >
              {TICKER_INTERVAL_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </>
        ) : (
          <span className="ticker-event-driven">On event</span>
        )}
      </div>

      <label className="ticker-toggle-switch">
        <input type="checkbox" checked={row.automated} onChange={onToggle} />
        <span className="ticker-slider" />
      </label>
    </div>
  );
}
